import { useImmerAtom } from 'jotai-immer'
import { noop } from 'lodash-es'
import { useContext, useEffect, useRef, useState } from 'react'
import { TextArea as AriaTextArea } from 'react-aria-components'
import { useTranslation } from 'react-i18next'
import { tv } from 'tailwind-variants'
import FluentDismiss20Filled from '~icons/fluent/dismiss-20-filled'
import FluentPerson24Filled from '~icons/fluent/person-24-filled'
import FluentSend24Filled from '~icons/fluent/send-24-filled'
import { UIButton } from 'src/components/ui/Button'
import Text from 'src/components/ui/Text'
import UserAvatar from 'src/components/UserAvatar'
import { useCommentsRedesignExperiment } from 'src/experiments/experiments/2605/commentsRedesign'
import { useMe } from 'src/services/helpers/user'
import { cx } from 'src/utils/cx'
import { track } from 'src/utils/tracker'
import { ensureLogIn } from 'src/utils/user'
import { CommentContext, ensureContext } from './context'
import { RepliedComment, ReplyTargetInlineLabel } from './RepliedComment'

const inputStyle = tv({
  base: 'px-2 py-2.5 min-h-10 max-h-20 overflow-auto whitespace-pre-line bg-transparent outline-none',
  variants: {
    // iOS Safari auto-zooms inputs with font-size < 16px, jolting the modal.
    treatment: {
      true: 'pointer-coarse:text-base',
    },
  },
})

/**
 * A comment composer (comment input)
 *
 * Should be used within a `<CommentProvider>`.
 */
export function CommentComposer(props: StyleProps) {
  const { t } = useTranslation(['comment'])
  const { me } = useMe()
  const [inputValue, setInputValue] = useState('')
  const inputRef = useRef<HTMLTextAreaElement>(null)

  const { composerStateAtom, sendMessage, TurnstileWidget, turnstileReady } =
    useContext(CommentContext) ?? ensureContext()
  const { isTreatment } = useCommentsRedesignExperiment()

  const [state, updateState] = useImmerAtom(composerStateAtom)

  useEffect(() => {
    if (state.replyTo) {
      inputRef.current?.focus()
    }
  }, [state.replyTo])

  return (
    <div
      className={cx('flex items-start gap-4 text-sm', props.className)}
      style={props.style}
    >
      <div className="flex-none">
        {me ? (
          <UserAvatar user={me} className="size-10" />
        ) : (
          <div className="grid place-content-center size-10 bg-zinc-200 rounded-full">
            <FluentPerson24Filled className="size-6" />
          </div>
        )}
      </div>
      <div
        className={cx(
          'flex-1 min-w-0 -mx-2 rounded-md ring-1 focus-within:outline outline-2 outline-offset-2',
          // Light
          'focus-within:bg-white ring-zinc-300 focus-within:ring-zinc-300/50',
          isTreatment ? 'outline-black/40' : 'outline-black',
          // Dark
          'dark:focus-within:bg-black dark:ring-zinc-600 dark:focus-within:ring-zinc-600/50',
          isTreatment ? 'dark:outline-white/40' : 'dark:outline-white',
        )}
      >
        {me ? (
          <>
            {state.replyTo &&
              (isTreatment ? (
                <div className="p-2 border-b border-zinc-300 dark:border-zinc-600 animate-in fade-in slide-in-from-top-1 duration-200 ease-out">
                  <div className="flex items-start gap-2">
                    <div className="flex-1 min-w-0">
                      <ReplyTargetInlineLabel
                        messageId={state.replyTo}
                        className="mb-1"
                      />
                      <RepliedComment messageId={state.replyTo} />
                    </div>
                    <button
                      aria-label="Cancel reply"
                      className="flex-none p-1 -m-1 hover:bg-black/5 dark:hover:bg-white/5 rounded text-neutral-500 hover:text-neutral-900 dark:hover:text-neutral-100"
                      onClick={() => {
                        updateState(state => {
                          state.replyTo = undefined
                        })
                      }}
                    >
                      <FluentDismiss20Filled className="size-5" />
                    </button>
                  </div>
                </div>
              ) : (
                <div className="flex items-start p-2 bg-black/5 dark:bg-white/5 border-b border-zinc-300 dark:border-zinc-600 rounded-t-md">
                  <RepliedComment
                    messageId={state.replyTo}
                    className="flex-1"
                  />
                  <button
                    className="flex-none p-1 -m-1 ml-0 hover:bg-black/5 dark:hover:bg-white/5 rounded"
                    onClick={() => {
                      updateState(state => {
                        state.replyTo = undefined
                      })
                    }}
                  >
                    <FluentDismiss20Filled className="size-5" />
                  </button>
                </div>
              ))}
            <TurnstileWidget />
            <div className="relative">
              <div
                className={inputStyle({
                  treatment: isTreatment,
                  className: 'invisible',
                })}
              >
                {inputValue}&zwj;
              </div>
              <AriaTextArea
                ref={inputRef}
                value={inputValue}
                placeholder={t('comment:send-message-placeholder')}
                className={inputStyle({
                  treatment: isTreatment,
                  className: 'absolute inset-0 h-full resize-none',
                })}
                onChange={ev => setInputValue(ev.target.value)}
              />
            </div>
          </>
        ) : (
          <button
            className={cx(
              inputStyle({ treatment: isTreatment, className: 'w-full' }),
              isTreatment &&
                'font-semibold text-purple-600 dark:text-purple-400',
            )}
            onClick={() => {
              track('login_comment_click')
              ensureLogIn({ fromPath: 'comment' }).catch(noop)
            }}
          >
            <Text i18nKey="comment:login-to-comment" />
          </button>
        )}
      </div>
      <UIButton
        variant="ghost"
        size="large"
        busy="auto"
        disabled={!inputValue.trim() || !turnstileReady}
        icon={<FluentSend24Filled className="size-6" />}
        className="flex-none grid-cols-1 place-items-center size-10"
        onPress={async () => {
          if (!inputValue.trim()) return
          try {
            await sendMessage(inputValue, state.replyTo)
            setInputValue('')
            updateState(state => {
              state.replyTo = undefined
            })
          } catch {
            // The necessary notification was already sent from `CommentContext`.
            // So we can do nothing here.
          }
        }}
      />
    </div>
  )
}
